import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import Icon from '@/components/ui/icon';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from '@/i18n';
import { hasMasterdataWritePermission } from '@/lib/masterdataPermissions';

interface MasterdataWorkbenchRouteGuardProps {
  backPath: string;
  children: ReactElement;
}

export default function MasterdataWorkbenchRouteGuard({
  backPath,
  children,
}: MasterdataWorkbenchRouteGuardProps) {
  const user = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (hasMasterdataWritePermission(user)) {
    return children;
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 lg:px-8">
      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            <span className="rounded-full bg-muted p-2 text-muted-foreground">
              <Icon name="ShieldAlert" className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="space-y-1">
              <CardTitle>{t('project.masterdata.accessDeniedTitle')}</CardTitle>
              <CardDescription>
                {t('project.masterdata.accessDeniedDescription')}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {t('project.masterdata.accessDeniedHint')}
        </CardContent>
        <CardFooter>
          <Button type="button" variant="outline" onClick={() => navigate(backPath)}>
            <Icon name="ArrowLeft" className="h-4 w-4" aria-hidden="true" />
            {t('project.masterdata.accessDeniedAction')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
